'use client';

import Image from 'next/image';
import { motion } from 'motion/react';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/global/hero_desktop_background-lg-2.png';
import type { PageGeneralRulesV3Schema } from '@/sanity/pages/PageGeneralRulesV3Template';

const ease = [0.25, 0.1, 0.25, 1] as const;

// "General" is Geist semibold; the serif word ("Rules") sits on a mint block.
const serif = ({ children }: { children?: React.ReactNode }) => (
  <span className="relative inline-block align-top font-blacker text-[4.75rem] font-normal italic leading-[inherit] tracking-[-0.1425rem] text-[#003828] lg:text-[6.75rem] lg:tracking-[-0.2rem]">
    {/* mint block: Figma 97×18 at +22 (mobile), 143×24 at +30 (desktop) */}
    <span
      className="absolute left-[-0.25rem] right-[-0.75rem] top-[2.75rem] -z-10 h-[2.25rem] bg-[#e2f3ef] lg:left-[-0.5rem] lg:right-[-1rem] lg:top-[3.75rem] lg:h-[3rem]"
      aria-hidden
    />
    {children}
  </span>
);

const titleComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="mb-0">{children}</p>,
    h1: ({ children }) => <p className="mb-0">{children}</p>,
    h2: ({ children }) => <p className="mb-0">{children}</p>,
  },
  marks: {
    darkGreenSerif: serif,
    lightGreenSerif: serif,
    greenSerifHighlight: serif,
    serif,
    em: serif,
    strong: ({ children }) => <>{children}</>,
  },
};

const bodyComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="mb-0">{children}</p>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-medium text-black">{children}</strong>,
  },
};

export default function HeroSection({ data }: { data: PageGeneralRulesV3Schema }) {
  return (
    <section
      data-ui="rules-v3.hero"
      className="relative w-full bg-white pb-[8rem] pt-[6rem] [text-wrap:wrap] lg:pb-[5rem] lg:pt-[12.5rem]"
    >
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute left-0 top-0 z-0 block h-full w-full lg:hidden"
      >
        <Image src={heroMobileBackground} alt="" className="h-full w-full object-cover object-top" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute left-0 top-[-8.5rem] z-0 hidden h-[77rem] w-full lg:block"
      >
        <Image src={heroDesktopBackground} alt="" className="h-full w-full object-cover object-center" />
      </motion.div>
      {/* fade so the ornament ends before the search bar, like the Figma "Degradê" */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[20rem] bg-gradient-to-b from-transparent to-white lg:h-[27.5rem]" />

      <div data-ui="hero.content" className="relative z-10 mx-auto flex w-[calc(100%-4rem)] flex-col items-center gap-[4rem] text-center lg:w-[95.75rem]">
        <div className="flex flex-col items-center gap-[1.5rem]">
          {data.heroChip && (
            <p data-ui="hero.chip" className="mb-0 font-blacker text-[2rem] italic leading-[3rem] text-black">{data.heroChip}</p>
          )}
          {data.heroTitle && (
            <div data-ui="hero.title" className="font-geist text-[5rem] font-semibold leading-[5.75rem] tracking-[-0.25rem] text-black lg:text-[7rem] lg:leading-[8rem] lg:tracking-[-0.35rem]">
              <PortableText value={data.heroTitle} components={titleComponents} />
            </div>
          )}
        </div>
        <div className="flex w-full flex-col items-center gap-[2rem] font-geist">
          {data.heroSubtitle && (
            <p data-ui="hero.subtitle" className="mb-0 whitespace-pre-line text-[2.25rem] font-medium leading-[3.25rem] text-black lg:text-[2.5rem] lg:leading-[3.5rem]">
              {data.heroSubtitle}
            </p>
          )}
          {data.heroBody && (
            <div data-ui="hero.body" className="flex w-full flex-col items-center gap-[2rem] text-[2rem] leading-[3rem] text-[#1c1f22] opacity-70 [&>p]:w-full lg:[&>p:first-child]:w-[88rem]">
              <PortableText value={data.heroBody} components={bodyComponents} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
